<script setup lang="ts">
import { storeToRefs } from 'pinia';
import dayjs from 'dayjs';
import RouletteDeliveryBadge from './RouletteDeliveryBadge.vue';
import { useRouletteStore } from '@/stores/roulette';
import { prizeDisplayImage } from '~/adapters/api-roulette/roulette.mappers';

const store = useRouletteStore();
const { historyItems, historyStatus, canLoadMoreHistory, prizes } =
  storeToRefs(store);

onMounted(() => {
  store.loadHistory();
});

// Linhas prontas pra render (uma passada por item, fora do template):
// - imagem: payload > lookup por id nos prêmios da roleta carregada (o back
//   ainda não envia as URLs no histórico; giros de outras roletas ficam sem)
// - data/hora locais do giro no formato "02/07/2026 · 15:54"
const rows = computed(() => {
  const prizeById = new Map(prizes.value.map((prize) => [prize.id, prize]));

  return historyItems.value.map((item) => ({
    key: item.spinId,
    // 3 linhas: prêmio, nome da roleta e data/hora do giro.
    title:
      item.result === 'lose'
        ? 'Não foi dessa vez'
        : item.prize?.prizeName || 'Prêmio',
    roulette: item.roulette?.title || '',
    meta: item.spunAt ? dayjs(item.spunAt).format('DD/MM/YYYY · HH:mm') : '',
    image:
      prizeDisplayImage(item.prize) ||
      prizeDisplayImage(prizeById.get(item.prize?.id ?? -1)),
    // Null na derrota (mapper absorve a convenção do back) — sem badge.
    deliveryStatus: item.deliveryStatus,
  }));
});
</script>

<template>
  <div class="rlt-history">
    <div
      v-if="historyStatus === 'loading'"
      class="rlt-history__state"
    >
      Carregando histórico...
    </div>

    <div
      v-else-if="historyStatus === 'error'"
      class="rlt-history__state"
    >
      <p>Não foi possível carregar seu histórico.</p>
      <button
        class="rlt-history__retry"
        type="button"
        @click="store.loadHistory()"
      >
        Tentar novamente
      </button>
    </div>

    <div
      v-else-if="historyItems.length === 0"
      class="rlt-history__state"
    >
      Você ainda não girou a roleta.
    </div>

    <template v-else>
      <ul class="rlt-history__list">
        <li
          v-for="row in rows"
          :key="row.key"
          class="rlt-history__item"
        >
          <div class="rlt-history__thumb">
            <img
              v-if="row.image"
              alt=""
              class="rlt-history__img"
              :src="row.image"
            />
          </div>

          <div class="rlt-history__info">
            <span class="rlt-history__prize">{{ row.title }}</span>
            <span
              v-if="row.roulette"
              class="rlt-history__meta"
              >{{ row.roulette }}</span
            >
            <span
              v-if="row.meta"
              class="rlt-history__meta"
              >{{ row.meta }}</span
            >
          </div>

          <RouletteDeliveryBadge
            v-if="row.deliveryStatus"
            compact
            :status="row.deliveryStatus"
          />
        </li>
      </ul>

      <button
        v-if="canLoadMoreHistory"
        class="rlt-history__more"
        :disabled="historyStatus === 'loadingMore'"
        type="button"
        @click="store.loadMoreHistory()"
      >
        {{
          historyStatus === 'loadingMore' ? 'Carregando...' : 'Carregar mais'
        }}
      </button>
    </template>
  </div>
</template>

<style scoped lang="scss">
.rlt-history {
  @apply w-full flex flex-col gap-3 pt-2;

  &__state {
    @apply flex flex-col items-center gap-3 text-white/80 text-sm text-center my-10;
  }

  &__retry {
    @apply text-white text-sm underline;
  }

  &__list {
    @apply flex flex-col gap-2;
  }

  &__item {
    @apply flex items-center gap-3 rounded-xl px-3 py-3;

    background: rgba(255, 255, 255, 0.06);
  }

  // Miniatura do prêmio: quadrado fixo à esquerda; segue visível (fundo sutil)
  // mesmo sem imagem, pra lista não desalinhar.
  &__thumb {
    @apply flex items-center justify-center shrink-0 rounded-lg overflow-hidden;

    width: 56px;
    height: 56px;
    padding: 4px;
    background: rgba(255, 255, 255, 0.08);
  }

  &__img {
    // contain (não cover): a arte do prêmio aparece inteira, centralizada no
    // quadrado, sem corte.
    @apply w-full h-full object-contain;
  }

  &__info {
    @apply flex flex-col gap-0.5 min-w-0 flex-1;
  }

  // Cada linha (prêmio, roleta, data) clampada em 1 linha com reticências.
  &__prize,
  &__meta {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
  }

  &__prize {
    @apply text-white text-sm font-semibold;
  }

  &__meta {
    @apply text-white/60 text-xs;
  }

  &__more {
    @apply mx-auto mt-1 rounded-full px-4 py-2 text-sm font-semibold text-white;

    background: rgba(255, 255, 255, 0.12);

    &:disabled {
      @apply opacity-60;
    }
  }
}
</style>
